import React from "react";
import ReactDOM from "react-dom/client";
import { CartWidget } from "../components/CartWidget";
import { ApiClient } from "../api/client";
import type { CartWidgetConfig } from "../types";
import { LocaleProvider } from "../i18n/LocaleProvider";
import { parseLocale, type Locale } from "../i18n";

// Import Tailwind CSS - sera injecté dans le DOM par vite-plugin-css-injected-by-js
import "../styles/globals.css";

/**
 * Détecte si on est en mode debug à partir de l'URL de l'API
 */
function isDevMode(apiUrl: string): boolean {
  try {
    const hostname = new URL(apiUrl).hostname;
    return hostname === "localhost" || hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

/**
 * Point d'entrée du widget Panier Bonjour.fun
 *
 * Le panier est chargé automatiquement via le cookie de session (bjr_session).
 * Aucun cartId n'est nécessaire — le widget détecte la session du client.
 *
 * Expose l'API globale BonjourCart
 */

class BonjourCartInstance {
  private root: ReactDOM.Root | null = null;
  private config: CartWidgetConfig | null = null;
  private apiClient: ApiClient | null = null;
  private locale: Locale = "fr-FR";

  /**
   * Initialise le widget dans le DOM
   */
  init(config: CartWidgetConfig): void {
    this.config = config;

    if (!config.container) {
      throw new Error("BonjourCart: container is required");
    }
    if (!config.apiKey) {
      throw new Error("BonjourCart: apiKey is required");
    }

    if (!config.apiUrl) {
      throw new Error("BonjourCart: apiUrl is required");
    }

    const apiUrl = config.apiUrl;
    const debug = config.debug ?? isDevMode(apiUrl);
    this.locale = parseLocale(config.locale);

    this.apiClient = new ApiClient(config.apiKey, apiUrl, debug);

    const container =
      typeof config.container === "string"
        ? document.querySelector(config.container)
        : config.container;

    if (!container) {
      throw new Error(`BonjourCart: container not found: ${config.container}`);
    }

    this.mount(container);

    // Appliquer le thème après le montage
    if (config.theme) {
      this.applyTheme(config.theme);
    }

    if (debug) {
      console.log("[BonjourCart] Initialized with config:", {
        ...config,
        apiUrl,
        debug,
        locale: this.locale,
      });
    }
  }

  private mount(container: Element): void {
    const reactContainer = document.createElement("div");
    reactContainer.className = "widget-root";
    container.appendChild(reactContainer);

    this.root = ReactDOM.createRoot(reactContainer);
    this.renderWidget();
  }

  private renderWidget(): void {
    if (!this.root || !this.config || !this.apiClient) {
      return;
    }

    this.root.render(
      <React.StrictMode>
        <LocaleProvider locale={this.locale}>
          <CartWidget
            apiClient={this.apiClient}
            onSuccess={this.config.onSuccess}
            onError={this.config.onError}
          />
        </LocaleProvider>
      </React.StrictMode>,
    );
  }

  /**
   * Applique le thème personnalisé
   */
  private applyTheme(theme: NonNullable<CartWidgetConfig["theme"]>): void {
    const root = document.documentElement;

    if (theme.primaryColor) {
      root.style.setProperty("--main-color", theme.primaryColor);
    }
    if (theme.borderRadius) {
      root.style.setProperty("--border-radius", theme.borderRadius);
    }
    if (theme.fontFamily) {
      root.style.setProperty("--font-family", theme.fontFamily);
    }
  }

  destroy(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    const debug = this.config?.debug;

    this.config = null;
    this.apiClient = null;

    if (debug) {
      console.log("[BonjourCart] Destroyed");
    }
  }
}

// Exposer l'API globale
const BonjourCart = {
  init: (config: CartWidgetConfig) => {
    const instance = new BonjourCartInstance();
    instance.init(config);
    return instance;
  },
};

// Exposer sur window
if (typeof window !== "undefined") {
  (window as any).BonjourCart = BonjourCart;
}

/**
 * Custom Element <bonjour-cart>
 *
 * Le panier est chargé automatiquement via le cookie de session.
 *
 * Usage:
 * <bonjour-cart
 *   data-url="https://branche.logiciel.codelsior.fr"
 *   data-api-key="pk_xxx"
 *   data-locale="fr-FR"                    (optionnel - par défaut: fr-FR)
 *   data-on-success="handleSuccess"      (optionnel)
 *   data-on-error="handleError"           (optionnel)
 *   data-theme-primary-color="#3b82f6"    (optionnel)
 *   data-theme-border-radius="0.5rem"     (optionnel)
 * ></bonjour-cart>
 */
class BonjourCartElement extends HTMLElement {
  private widgetInstance: BonjourCartInstance | null = null;

  static get observedAttributes() {
    return ["data-url", "data-api-key", "data-locale"];
  }

  connectedCallback() {
    requestAnimationFrame(() => this.initWidget());
  }

  disconnectedCallback() {
    this.widgetInstance?.destroy();
    this.widgetInstance = null;
  }

  attributeChangedCallback() {
    if (this.widgetInstance) {
      this.widgetInstance.destroy();
      this.initWidget();
    }
  }

  private initWidget() {
    const apiUrl = this.dataset.url;
    const apiKey = this.dataset.apiKey;

    if (!apiUrl || !apiKey) {
      console.error("[bonjour-cart] data-url et data-api-key sont requis");
      return;
    }

    const onSuccessName = this.dataset.onSuccess;
    const onErrorName = this.dataset.onError;

    const onSuccess =
      onSuccessName && typeof (window as any)[onSuccessName] === "function"
        ? (window as any)[onSuccessName]
        : undefined;

    const onError =
      onErrorName && typeof (window as any)[onErrorName] === "function"
        ? (window as any)[onErrorName]
        : undefined;

    const theme: CartWidgetConfig["theme"] = {};
    if (this.dataset.themePrimaryColor)
      theme.primaryColor = this.dataset.themePrimaryColor;
    if (this.dataset.themeBorderRadius)
      theme.borderRadius = this.dataset.themeBorderRadius;
    if (this.dataset.themeFontFamily)
      theme.fontFamily = this.dataset.themeFontFamily;

    const locale = this.dataset.locale;

    this.widgetInstance = new BonjourCartInstance();
    this.widgetInstance.init({
      container: this,
      apiUrl,
      apiKey,
      onSuccess,
      onError,
      locale,
      theme: Object.keys(theme).length > 0 ? theme : undefined,
    });
  }
}

// Enregistrer le Custom Element
if (typeof window !== "undefined" && !customElements.get("bonjour-cart")) {
  customElements.define("bonjour-cart", BonjourCartElement);
}

export default BonjourCart;
